import React, { type JSX, useCallback } from 'react';
import css from '@styled-system/css';
import styled, { CSSObject } from 'styled-components';
import { compose, layout, space, variant } from 'styled-system';

import {
  fontWeight as fontWeightSystem,
  textAlign,
  textColor,
  textDecoration,
  textTransform,
} from '@rover/kibble/styles/styled-system';

import roverTheme from '../../themes/roverTheme';
import Box from '../Box';
import { PolymorphicBoxPropsHelper } from '../Box/Box.common';

import { TextCommonProps } from './Text.common';

export type Props = TextCommonProps;

const StyledText = styled(Box)<Omit<Props, 'as'>>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
  },
  (props) => {
    const text: Record<string, { fontWeight: number; fontSize: string; lineHeight: number }> = props
      .theme?.text
      ? props.theme.text
      : roverTheme.text;
    // the theme has fontWeights, but historically we didn't have them in the hand rolled size variant styles
    // so now, to prevent a bunch of strange style issues, we remove the fontWeight styles here
    const textWithoutFontWeights = Object.fromEntries(
      // var is unused to remove it from the final object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(text).map(([key, { fontWeight, ...value }]) => [key, value])
    );
    return variant({
      prop: 'size',
      variants: textWithoutFontWeights,
    })(props);
  },
  compose(textAlign, textColor, fontWeightSystem, textDecoration, textTransform, layout, space), // Apply last to avoid being overwritten by resets and variant styles

  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme) as CSSObject
);

/** Inline and UI text, such as a label or a value. */
function Text<C extends React.ElementType = 'span'>({
  as,
  size = '200',
  onPress,
  webProps = {},
  ...other
}: PolymorphicBoxPropsHelper<Props, C>): JSX.Element {
  const { as: webAs, ...otherWebProps } = webProps;
  const Component = as || webAs || 'span';
  const handlePress = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (onPress) {
        onPress({ webEvent: event });
      }
    },
    [onPress]
  );

  return (
    <StyledText
      {...other}
      {...otherWebProps}
      webAs={Component}
      size={size}
      onClick={onPress ? handlePress : undefined}
    />
  );
}

export default Text;
